import {
  alpha,
  Box,
  Button,
  makeStyles,
  Popover,
  Typography,
  useTheme,
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import {
  bindPopover,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import { useSnackbar } from 'notistack';

import { useAppDispatch, useAppSelector } from 'store';
import { CanalInterface } from 'store/reducers/canais';
import {
  gruposSelector,
  setCanalSelecionadoId,
  setChannelPreference,
} from 'store/reducers/grupos';
import getIconTipoCanal from 'utils/getIconTipoCanal';

import CanalMenuItem from './CanalMenuItem';

const useStyles = makeStyles(({ palette, spacing }) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1),
  },
  popoverPaper: {
    marginTop: spacing(1),
    width: 300,
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
    padding: spacing(1.5),
  },
  popoverTitle: {
    color: palette.text.secondary,
    maxWidth: 260,
    marginBottom: spacing(2),
  },
}));

interface SelectorCanalEnvioProps {
  canais: CanalInterface[];
  selectedCanal: CanalInterface | undefined;
}

export default function SelectorCanalEnvio({
  canais,
  selectedCanal,
}: SelectorCanalEnvioProps) {
  const classes = useStyles();
  const { palette } = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const { loadingCanalPreferencia, grupoSelecionado } =
    useAppSelector(gruposSelector);

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'selector-canal-envio',
  });

  const handleSelect = (canal: CanalInterface) => {
    if (!grupoSelecionado) return;

    dispatch(
      setChannelPreference({
        grupoId: grupoSelecionado.id,
        canalId: canal.id,
      }),
    )
      .unwrap()
      .then(({ canalId }) => {
        dispatch(setCanalSelecionadoId(canalId));
        enqueueSnackbar('Canal principal definido com sucesso.');
        popupState.close();
      })
      .catch(error => {
        enqueueSnackbar(error.message, { variant: 'error' });
      });
  };

  const currentCanal = selectedCanal || canais[0];
  if (!currentCanal) return null;

  return (
    <Box className={classes.root}>
      <Button
        style={{
          maxWidth: 180,
          backgroundColor: currentCanal.isCanalOnline
            ? undefined
            : alpha(palette.error.main, 0.1),
        }}
        startIcon={
          <Box display="flex" alignItems="center">
            {getIconTipoCanal({
              tipoCanal: currentCanal.tipoCanal,
              size: 20,
              isModoHibrido: currentCanal.isModoHibrido,
            })}
          </Box>
        }
        endIcon={<ArrowDropDownIcon />}
        variant="outlined"
        disabled={loadingCanalPreferencia}
        {...bindTrigger(popupState)}
      >
        <Typography variant="body2" noWrap>
          {currentCanal.nome}
        </Typography>
      </Button>

      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        classes={{ paper: classes.popoverPaper }}
      >
        <Typography className={classes.popoverTitle} variant="body2">
          Selecione o canal para enviar as próximas mensagens.
        </Typography>

        {canais.map(canal => (
          <CanalMenuItem
            key={canal.id}
            canal={canal}
            isSelected={canal.id === currentCanal.id}
            disabled={loadingCanalPreferencia}
            onClick={() => handleSelect(canal)}
          />
        ))}
      </Popover>
    </Box>
  );
}
